import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowClockwise, DownloadSimple, WarningCircle } from 'shared/icons';
import api from '../../services/api';
import { DownloadCsv } from '../../utils/csv';
import { FormatBrl, FormatDecimal1, FormatInt } from '../../utils/format';
import PaginationControls from '../PaginationControls';
import Button from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';
import CampaignItemCard from './CampaignItemCard';
import CampaignMonitorFilters from './CampaignMonitorFilters';
import { ComputeItemFees, IsNum } from './campaignCardShared';
import MlShortcutsMenu from './MlShortcutsMenu';

// Tarefa 2: Filtros & busca agora são CLIENT-SIDE para instantaneidade.
// - Carregamos TODOS os itens (261 ativos cabem em memória com folga)
// - Filtros/busca fazem recorte em memória, sem request
// - STATUS é exceção: continua SERVER-SIDE (muda o universo carregado)
// Debounce foi removido — a busca é EXPLÍCITA: o texto digitado é rascunho e só
// vira filtro no Enter (ou ao esvaziar o campo). Antes o `searchInput` não era
// ligado a `filters.search` em lugar nenhum: dava para digitar o dia inteiro
// que a lista não mudava.

// Tarefa 2: API_MAX_LIMIT e CSV_MAX_PAGES foram removidos.
// CSV agora vem direto do array filtrado em memória, sem paginação.

const DEFAULT_FILTERS = {
    status: 'active',
    sortBy: 'sales_desc',
    catalogStatus: 'all',
    hasCampaign: 'all',
    rebate: '',
    brand: '',
    categoryId: '',
    campaignType: '',
    campaignId: '',
    stock: '',
    outdated: '',
    search: '',
    limit: 30,
    offset: 0,
};

const EMPTY_OPTIONS = { brands: [], categories: [], campaign_types: [], campaigns: [] };

// Dimensões que se ajustam no PAINEL "Filtros". Um preset (card da Visão geral)
// que mexe numa delas abre o painel na montagem: com ele fechado a lista
// chegaria recortada — "Com rebate", por exemplo — sem nada na tela dizendo por
// quê, e o usuário leria o recorte como o universo inteiro.
const PANEL_FILTER_KEYS = ['rebate', 'brand', 'categoryId', 'campaignType', 'campaignId', 'stock', 'outdated'];

// "Prioridade: BuyBox" é ordem de AÇÃO, não alfabética: quem está perdendo a
// disputa pede providência primeiro, quem divide vem em seguida e quem já
// ganha pode esperar. Qualquer outro status (fora do catálogo, sem disputa)
// não tem o que priorizar e desce para o fim da fila.
const BUYBOX_SORT_RANK = { competing: 0, sharing_first_place: 1, winning: 2 };
const BUYBOX_SORT_RANK_DEFAULT = 3;

/**
 * Query para CARREGAR TUDO (primeira vez). Wire em `snake_case` (CLAUDE.md §2).
 * NUNCA inclui offset/limit: carrega o universo inteiro do status selecionado.
 * Apenas status continua SERVER-SIDE (Tarefa 2: filtros/busca agora client).
 */
function BuildFullItemsQuery(filters) {
    const params = new URLSearchParams({
        // `all` faz parte do contrato: o servidor o entende como "sem filtro
        // de status", então o valor do select vai sempre literal no wire.
        status: filters.status,
        limit: '1000', // Cap da API, suficiente para 261 itens
        offset: '0',
    });
    return params.toString();
}


// ── formatadores do CSV ─────────────────────────────────────────────────────
// Ausente vira célula VAZIA (não '—'): numa planilha o travessão contamina a
// coluna e impede somar. Números saem em pt-BR ("1.234,56") porque o arquivo é
// gerado para o Excel pt-BR — ver `utils/csv.js`.
const csvMoney = (value) => (IsNum(value) ? FormatBrl(value) : '');
const csvDecimal = (value) => (IsNum(value) ? FormatDecimal1(value) : '');
const csvInt = (value) => (IsNum(value) ? String(Number(value)) : '');
const csvBool = (value) => (value === true ? 'Sim' : value === false ? 'Não' : '');
const currentCampaignOf = (item) => (Array.isArray(item.campaigns) ? item.campaigns : []).find((c) => c?.is_current);

/**
 * Colunas do CSV = tudo o que o CARD mostra, na mesma ordem de leitura
 * (identidade → preços → campanha → catálogo → estoques → vendas → tarifas).
 * O conteúdo EXPANDIDO (lista de campanhas e tabela de concorrentes) fica de
 * fora: é uma relação 1:N e não cabe numa linha por anúncio.
 */
const CSV_COLUMNS = [
    { label: 'MLB', getValue: (item) => item.item_id || '' },
    { label: 'SKU', getValue: (item) => item.sku || '' },
    { label: 'Título', getValue: (item) => item.title || '' },
    { label: 'Status do anúncio', getValue: (item) => item.ml_status || '' },
    { label: 'Catálogo', getValue: (item) => csvBool(item.catalog_listing) },
    { label: 'Atenção', getValue: (item) => item.attention_status || '' },
    { label: 'Preço base', getValue: (item) => csvMoney(item.price_base) },
    { label: 'Preço consumer', getValue: (item) => csvMoney(item.price_consumer) },
    { label: 'Rebate %', getValue: (item) => csvDecimal(item.rebate_percent) },
    { label: 'Rebate R$', getValue: (item) => csvMoney(item.rebate_amount) },
    { label: 'Líquido', getValue: (item) => csvMoney(item.price_net) },
    // Mesma origem do prefixo da linha "Líquido" no card: o desconto da campanha ATUAL.
    { label: 'Desconto %', getValue: (item) => csvDecimal(currentCampaignOf(item)?.discount_percent) },
    { label: 'Campanha atual', getValue: (item) => item.campaign_name || item.campaign_id || '' },
    { label: 'Tipo da campanha', getValue: (item) => item.campaign_type || '' },
    { label: 'Status na campanha', getValue: (item) => item.campaign_item_status || '' },
    { label: 'Campanhas elegíveis', getValue: (item) => csvInt(item.campaigns_available_count) },
    { label: 'Campanhas novas', getValue: (item) => csvInt(item.new_campaigns_count) },
    { label: 'Status catálogo', getValue: (item) => item.buybox_status || '' },
    { label: 'Concorrentes', getValue: (item) => csvInt(item.competitors_count) },
    { label: 'Melhor campanha', getValue: (item) => item.best_campaign?.campaign_name || '' },
    { label: 'Ganho da melhor', getValue: (item) => csvMoney(item.best_campaign?.net_gain) },
    { label: 'Já está na melhor', getValue: (item) => csvBool(item.is_on_best_campaign) },
    { label: 'Estoque do anúncio', getValue: (item) => csvInt(item.stock_ml_available) },
    { label: 'Full do SKU', getValue: (item) => csvInt(item.stock_sku_fulfillment) },
    { label: 'Estoque ML indisponível', getValue: (item) => csvInt(item.stock_ml_unavailable) },
    { label: 'Estoque Bling', getValue: (item) => csvInt(item.stock_bling) },
    { label: 'Vendas 24h', getValue: (item) => csvInt(item.sales_24h) },
    { label: 'Vendas média/dia (7d)', getValue: (item) => csvDecimal(item.sales_7d_avg) },
    { label: 'Melhor dia (data)', getValue: (item) => item.sales_7d_best_day?.date || '' },
    { label: 'Melhor dia (qtd)', getValue: (item) => csvInt(item.sales_7d_best_day?.qty) },
    { label: 'Vendas total 7d', getValue: (item) => csvInt(item.sales_7d_total) },
    { label: 'Variação 7d %', getValue: (item) => csvDecimal(item.sales_trend_percent) },
    { label: 'Tarifa ML %', getValue: (item) => csvDecimal(item.fee_percentage) },
    { label: 'Tarifa ML R$', getValue: (item) => csvMoney(ComputeItemFees(item).feeMlDisplay) },
    { label: 'Tarifa fixa', getValue: (item) => csvMoney(item.fee_fixed) },
    { label: 'Frete', getValue: (item) => csvMoney(item.shipping_cost) },
    { label: 'Tarifas total', getValue: (item) => csvMoney(ComputeItemFees(item).feeTotal) },
    { label: 'Tarifas total %', getValue: (item) => csvDecimal(ComputeItemFees(item).feeTotalPct) },
    { label: 'Permalink', getValue: (item) => item.permalink || '' },
];

/**
 * `initialFilters` = preset de quem abriu o Monitor (os cards da Visão geral:
 * `{ rebate: 'with' }`, `{ status: 'active' }`). Só vale na MONTAGEM — depois o
 * usuário é dono dos filtros, e o "Limpar" volta ao padrão, não ao preset.
 */
const CampaignMonitorItems = ({ initialFilters = null }) => {
    // Tarefa 2: refatoring para filtros/busca CLIENT-SIDE
    // - allItems: todos os itens do status atual (carregado 1x)
    // - items: slice paginado do filtrado (o que mostra na tela)
    const [allItems, setAllItems] = useState([]);
    // Filtrar no cliente só é honesto se o cliente TIVER tudo. Guarda contra o
    // dia em que o catálogo passar do teto da API (1000): sem isto, a resposta
    // viria cortada e a tela mostraria base parcial com contagens de chip que
    // parecem completas — o número mentiria sem ninguém perceber.
    const [truncado, setTruncado] = useState(null);
    const [items, setItems] = useState([]);
    const [totalFiltered, setTotalFiltered] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [showPanel, setShowPanel] = useState(() => PANEL_FILTER_KEYS.some((key) => Boolean(initialFilters?.[key])));
    const [options, setOptions] = useState(EMPTY_OPTIONS);
    // A busca vive em `filters.search`, sem estado intermediário nem debounce:
    // o recorte é em memória, então cada tecla pode refiltrar na hora — e um
    // único dono do valor garante que o que o input mostra é o que filtra.
    // Rascunho da busca: o que está no campo. Só vira `filters.search` no Enter
    // (ou ao esvaziar) — ver handleSearchChange/submitSearch.
    const [searchInput, setSearchInput] = useState('');
    const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS, ...(initialFilters || {}) }));

    // DEEP-LINK vindo da notificação (ou de qualquer link):
    // /campaigns?tab=items&item_id=MLB…&panel=historico. Põe o MLB na busca
    // (o usuário vê e pode limpar), abre o universo `all` — o anúncio pode
    // estar pausado/encerrado e sumiria do recorte `active` — e diz ao card
    // qual painel abrir. Reage a mudanças de URL, não só à montagem: quem já
    // está no monitor e clica noutra notificação precisa ser levado ao novo card.
    //
    // Com PRESET, o deep-link que já estava na URL ao montar é resto de uma
    // navegação anterior (a aba trocada pelo clique não mexe na URL): aplicá-lo
    // somaria busca pelo MLB + universo `all` ao recorte que o card pediu, e a
    // tela mostraria um anúncio só. O preset é o pedido mais novo e ganha; um
    // deep-link NOVO (a URL mudou depois da montagem) volta a valer normalmente.
    //
    // A trava só vale até a URL mudar pela primeira vez: depois disso, voltar à
    // MESMA URL do resto (notificação A → B → A) é um pedido novo e tem de ser
    // atendido. Ref, e não "pular a primeira execução do efeito": no
    // StrictMode o efeito roda duas vezes na montagem e o pulo gastaria na
    // primeira, aplicando o resto na segunda.
    const location = useLocation();
    const staleSearchAtMount = useRef(initialFilters ? location.search : null);
    const [focus, setFocus] = useState(null);
    useEffect(() => {
        if (staleSearchAtMount.current !== null) {
            if (location.search === staleSearchAtMount.current) {
                return;
            }
            staleSearchAtMount.current = null;
        }
        const params = new URLSearchParams(location.search);
        const itemId = String(params.get('item_id') || '').trim();
        if (!itemId) {
            return;
        }
        const panel = params.get('panel') || 'historico';
        // A busca tem rascunho (campo) e valor aplicado (filtro): o deep-link
        // preenche os dois, senão o campo mostraria vazio com a lista filtrada.
        // Parte do PADRÃO (só a paginação escolhida fica): um recorte anterior
        // — preset de card ou filtro manual, "Com rebate", uma marca — somado
        // à busca pelo MLB esconderia justamente o anúncio que a notificação
        // mandou abrir.
        setSearchInput(itemId);
        setFilters((prev) => ({ ...DEFAULT_FILTERS, limit: prev.limit, search: itemId, status: 'all', offset: 0 }));
        setFocus({ itemId, panel });
    }, [location.search]);

    // Tarefa 2: LoadAllItems — carrega TODOS os itens do status uma vez
    // O servidor só conhece UMA dimensão: o status, que define o universo
    // carregado. Todo o resto é filtrado em memória. Recebendo o status por
    // ARGUMENTO em vez de fechar sobre `filters`, a dependência do hook fica
    // honesta — sem precisar silenciar o exhaustive-deps, que é o tipo de
    // silêncio que esconde re-render perdido meses depois.
    const LoadAllItems = useCallback(
        async (status) => {
            try {
                setLoading(true);
                setError(null);

                // NUNCA paginado: carrega o universo inteiro
                const query = BuildFullItemsQuery({ status });
                const response = await api.get(`/campaigns/items?${query}`);
                const payload = response?.data ?? response;

                // "Desatualizado" = o ML recusou a última consulta de promoções
                // deste anúncio. O wire manda o erro cru (`promotions_sync_error`);
                // o booleano é derivado UMA vez aqui para que filtro, chip e
                // contagem comparem sempre o mesmo campo, sem reinterpretações.
                const results = (Array.isArray(payload?.results) ? payload.results : []).map((item) => ({
                    ...item,
                    is_outdated: Boolean(item.promotions_sync_error),
                }));
                setAllItems(results);
                setTotalFiltered(results.length);
                const totalNoServidor = Number(payload?.paging?.total ?? results.length);
                setTruncado(
                    totalNoServidor > results.length
                        ? { carregados: results.length, total: totalNoServidor }
                        : null,
                );
            } catch (err) {
                console.error('Erro ao carregar itens:', err);
                setError('Nao foi possivel carregar os itens monitorados.');
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    // Carrega tudo quando status muda
    useEffect(() => {
        LoadAllItems(filters.status);
    }, [LoadAllItems, filters.status]);

    // Opções dos selects: uma vez só. Elas descrevem o catálogo inteiro (não a
    // página), então não dependem dos filtros — recarregar a cada mudança seria
    // um request por clique sem nenhum ganho.
    useEffect(() => {
        let active = true;
        api.get('/campaigns/items/filters')
            .then((response) => {
                const payload = response?.data ?? response;
                if (!active || !payload) {
                    return;
                }
                // Só aceita array: uma chave que voltou null derrubaria o
                // `.map()` do select e, com ele, a página inteira.
                setOptions(
                    Object.fromEntries(
                        Object.entries(EMPTY_OPTIONS).map(([key, fallback]) => [
                            key,
                            Array.isArray(payload[key]) ? payload[key] : fallback,
                        ]),
                    ),
                );
            })
            .catch((err) => {
                // Falhar aqui NÃO pode derrubar a lista: sem opções os selects
                // ficam só com "Todas", e todo o resto do monitor segue de pé.
                console.error('Erro ao carregar opções de filtro:', err);
            });
        return () => {
            active = false;
        };
    }, []);

    // Tarefa 2: Filtragem CLIENT-SIDE em DUAS camadas. A primeira (`scopedItems`)
    // aplica só as dimensões que os chips NÃO controlam — busca, rebate, marca,
    // categoria e campanha. Ela existe separada porque é a base das contagens
    // facetadas: cada chip conta "quantos itens este chip mostraria", e para
    // isso a base não pode já estar cortada pelo chip ativo.
    const scopedItems = useMemo(() => {
        let result = allItems;

        // Busca: MLB, SKU ou título (case-insensitive)
        if (filters.search) {
            const term = filters.search.toLowerCase();
            result = result.filter((item) => {
                const itemId = String(item.item_id || '').toLowerCase();
                const sku = String(item.sku || '').toLowerCase();
                const title = String(item.title || '').toLowerCase();
                return itemId.includes(term) || sku.includes(term) || title.includes(term);
            });
        }

        // rebate
        if (filters.rebate === 'with') {
            result = result.filter((item) => item.rebate_percent && item.rebate_percent > 0);
        } else if (filters.rebate === 'without') {
            result = result.filter((item) => !item.rebate_percent || item.rebate_percent === 0);
        }

        // brand
        if (filters.brand) {
            result = result.filter((item) => item.brand === filters.brand);
        }

        // category_id
        if (filters.categoryId) {
            result = result.filter((item) => item.category_id === filters.categoryId);
        }

        // campaign_type
        if (filters.campaignType) {
            result = result.filter((item) => item.campaign_type === filters.campaignType);
        }

        // campaign_id
        if (filters.campaignId) {
            result = result.filter((item) => item.campaign_id === filters.campaignId);
        }

        return result;
    }, [
        allItems,
        filters.search,
        filters.rebate,
        filters.brand,
        filters.categoryId,
        filters.campaignType,
        filters.campaignId,
    ]);

    // Segunda camada: as dimensões dos chips (campanha / catálogo / estoque /
    // desatualizado), aplicadas sobre o recorte fixo. O resultado é a lista
    // que a tela mostra.
    const filteredItems = useMemo(() => {
        let result = scopedItems;

        // has_campaign (Com/Sem campanha)
        if (filters.hasCampaign !== 'all') {
            result = result.filter((item) => {
                const hasCampaign = item.campaign_id || item.campaign_name;
                const hasBetter = item.best_campaign;
                if (filters.hasCampaign === 'yes') {
                    return hasCampaign;
                }
                if (filters.hasCampaign === 'no') {
                    return !hasCampaign;
                }
                if (filters.hasCampaign === 'better') {
                    return hasBetter;
                }
                return true;
            });
        }

        // catalog_status: "No/Fora do catálogo" pergunta se o anúncio SEQUER é
        // de catálogo (`catalog_listing`), enquanto os demais valores perguntam
        // COMO vai a disputa (`buybox_status`) — são dois campos distintos.
        if (filters.catalogStatus === 'in_catalog') {
            result = result.filter((item) => Boolean(item.catalog_listing));
        } else if (filters.catalogStatus === 'not_in_catalog') {
            result = result.filter((item) => !item.catalog_listing);
        } else if (filters.catalogStatus !== 'all') {
            result = result.filter((item) => item.buybox_status === filters.catalogStatus);
        }

        // stock (Bling inventory)
        if (filters.stock === 'with') {
            result = result.filter((item) => item.stock_bling && item.stock_bling > 0);
        } else if (filters.stock === 'without') {
            result = result.filter((item) => !item.stock_bling || item.stock_bling === 0);
        } else if (filters.stock === 'low') {
            result = result.filter((item) => item.stock_bling && item.stock_bling < 10);
        }

        // outdated (último sync falhou)
        if (filters.outdated === 'yes') {
            result = result.filter((item) => item.is_outdated === true);
        }

        return result;
    }, [
        scopedItems,
        filters.hasCampaign,
        filters.catalogStatus,
        filters.stock,
        filters.outdated,
    ]);

    // Ordenação CLIENT-SIDE sobre o filtrado. Item sem valor no campo de texto
    // desce para o fim (uma fila de "Campanha" liderada por quem não tem
    // campanha só atrapalha), e TODO critério desempata por `item_id` para a
    // ordem ser estável entre re-renders — sem isso a paginação embaralharia.
    const sortedItems = useMemo(() => {
        const salesOf = (item) => (IsNum(item.sales_7d_total) ? Number(item.sales_7d_total) : 0);
        const compareItemId = (a, b) => String(a.item_id || '').localeCompare(String(b.item_id || ''));
        const compareText = (a, b) => {
            if (!a && !b) {
                return 0;
            }
            if (!a) {
                return 1;
            }
            if (!b) {
                return -1;
            }
            return String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base' });
        };

        const textFieldOf = {
            name: (item) => item.title || '',
            sku: (item) => item.sku || '',
            category: (item) => item.category_id || '',
            campaign: (item) => item.campaign_name || item.campaign_id || '',
        }[filters.sortBy];

        const result = [...filteredItems];
        if (filters.sortBy === 'buybox_losing') {
            // Dentro de cada grupo de disputa, vendas decidem: entre dois
            // perdendo, o que mais vende é o que mais dinheiro está deixando.
            const rankOf = (item) => BUYBOX_SORT_RANK[item.buybox_status] ?? BUYBOX_SORT_RANK_DEFAULT;
            result.sort((a, b) => rankOf(a) - rankOf(b) || salesOf(b) - salesOf(a) || compareItemId(a, b));
        } else if (textFieldOf) {
            result.sort((a, b) => compareText(textFieldOf(a), textFieldOf(b)) || compareItemId(a, b));
        } else {
            // 'sales_desc' é o padrão — e também o fallback de qualquer valor
            // desconhecido, para a lista nunca sair sem ordem definida.
            result.sort((a, b) => salesOf(b) - salesOf(a) || compareItemId(a, b));
        }
        return result;
    }, [filteredItems, filters.sortBy]);

    // Contagens FACETADAS dos chips (mesma semântica do antigo endpoint
    // `/items/quick-counts`): cada chip conta sobre `scopedItems` — o recorte
    // com busca e painel aplicados, mas SEM as dimensões dos próprios chips.
    // Contar sobre a lista visível zeraria os chips vizinhos assim que um
    // fosse ativado (os chips são exclusivos), e o número deixaria de
    // responder "o que eu veria se clicasse aqui".
    const quickCounts = useMemo(() => {
        const countWithCampaign = scopedItems.filter((item) => item.campaign_id || item.campaign_name).length;
        const countWithoutCampaign = scopedItems.filter((item) => !item.campaign_id && !item.campaign_name).length;
        // Mesmo predicado do filtro `hasCampaign === 'better'` acima.
        const countBetterCampaign = scopedItems.filter((item) => Boolean(item.best_campaign)).length;
        const countWinning = scopedItems.filter((item) => item.buybox_status === 'winning').length;
        const countCompeting = scopedItems.filter((item) => item.buybox_status === 'competing').length;
        const countSharing = scopedItems.filter(
            (item) => item.buybox_status === 'sharing_first_place',
        ).length;
        const countLowStock = scopedItems.filter((item) => item.stock_bling && item.stock_bling < 10).length;
        const countOutdated = scopedItems.filter((item) => item.is_outdated === true).length;

        return {
            with_campaign: countWithCampaign,
            without_campaign: countWithoutCampaign,
            better_campaign: countBetterCampaign,
            winning: countWinning,
            competing: countCompeting,
            sharing_first_place: countSharing,
            low_stock: countLowStock,
            outdated: countOutdated,
        };
    }, [scopedItems]);

    // Tarefa 2: Preenche o slice paginado (`items`) a partir do filtrado E
    // ordenado em memória — a página é um recorte da fila já na ordem final.
    useEffect(() => {
        const start = filters.offset;
        const end = start + filters.limit;
        const paginatedItems = sortedItems.slice(start, end);
        setItems(paginatedItems);
        setTotalFiltered(sortedItems.length);
    }, [sortedItems, filters.offset, filters.limit]);

    // Tarefa 2: Mudança de filtro volta para a primeira página. Nenhum patch
    // fala com o servidor por si — a recarga acontece só quando o VALOR de
    // `filters.status` muda (efeito acima), então digitar na busca ou trocar
    // um select refiltra em memória sem request.
    const patchFilters = useCallback((patch) => {
        setFilters((prev) => ({ ...prev, ...patch, offset: 0 }));
    }, []);

    // Enter é o gesto que APLICA a busca. Esvaziar o campo aplica na hora: "ver
    // tudo de novo" é o único caso em que exigir Enter só atrapalharia (e o X
    // nativo do `type="search"` chega aqui como mudança para string vazia).
    const handleSearchChange = useCallback(
        (value) => {
            setSearchInput(value);
            if (value === '') {
                patchFilters({ search: '' });
            }
        },
        [patchFilters],
    );

    const submitSearch = useCallback(() => {
        patchFilters({ search: searchInput.trim() });
    }, [patchFilters, searchInput]);

    const resetFilters = useCallback(() => {
        setFilters((prev) => ({ ...DEFAULT_FILTERS, limit: prev.limit, offset: 0 }));
    }, []);

    const handlePageChange = (page) => {
        setFilters((prev) => ({ ...prev, offset: (page - 1) * prev.limit }));
    };

    const handleItemsPerPageChange = (newLimit) => {
        setFilters((prev) => ({ ...prev, limit: newLimit, offset: 0 }));
    };

    // Tarefa 2: CSV vem direto do array em memória, na MESMA ordem da tela —
    // o arquivo é o retrato do que o usuário está vendo. Nenhum request.
    const exportCsv = useCallback(async () => {
        setExporting(true);
        try {
            const today = new Date().toISOString().slice(0, 10);
            DownloadCsv({ rows: sortedItems, columns: CSV_COLUMNS, fileName: `anuncios-${today}` });
        } catch (err) {
            console.error('Erro ao exportar CSV:', err);
            setError('Nao foi possivel exportar o CSV.');
        } finally {
            setExporting(false);
        }
    }, [sortedItems]);

    // ML vem PRIMEIRO, depois Download, depois Atualizar (ordem pedida pelo dono).
    const actions = useMemo(
        () => (
            <div className="flex items-center gap-2">
                <MlShortcutsMenu />
                <button
                    type="button"
                    onClick={exportCsv}
                    disabled={exporting || totalFiltered === 0}
                    title="Baixar CSV com os anúncios filtrados"
                    className="flex items-center gap-1.5 rounded-lg border border-mono-300 bg-white px-2.5 py-1.5 text-xs font-medium text-mono-700 transition-colors hover:border-mono-400 hover:text-black disabled:cursor-not-allowed disabled:opacity-50 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-200 dark:hover:border-mono-500 dark:hover:text-white"
                >
                    <DownloadSimple size={14} aria-hidden="true" />
                    {exporting ? 'Gerando...' : 'Download'}
                </button>
                {/* `() => LoadAllItems(filters.status)` e não `LoadAllItems`:
                    passado direto, o React manda o EVENTO de clique como
                    `status` e a query saía `status=[object Object]` — o botão
                    "recarregava" para uma lista vazia e só o F5 funcionava. */}
                <Button onClick={() => LoadAllItems(filters.status)} size="sm" disabled={loading}>
                    <ArrowClockwise size={16} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                    Atualizar
                </Button>
            </div>
        ),
        [exportCsv, exporting, totalFiltered, LoadAllItems, filters.status, loading],
    );

    return (
        <div className="space-y-3">
            {truncado ? (
                <p className="rounded-lg border border-danger-300 bg-danger-soft/50 px-3 py-2 text-[11px] text-danger-700 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-300">
                    Carregamos {truncado.carregados} de {truncado.total} anúncios — a resposta veio truncada pelo teto da
                    API. Os filtros e as contagens abaixo valem só para o que foi carregado, não para a base inteira.
                </p>
            ) : null}
            <CampaignMonitorFilters
                filters={filters}
                options={options}
                quickCounts={quickCounts}
                searchInput={searchInput}
                appliedSearch={filters.search}
                onSearchChange={handleSearchChange}
                onSearchSubmit={submitSearch}
                onPatch={patchFilters}
                onReset={resetFilters}
                showPanel={showPanel}
                onTogglePanel={() => setShowPanel((value) => !value)}
                actions={actions}
            />

            {/* Lista de Itens */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <LoadingSpinner />
                </div>
            ) : error ? (
                <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-xl border border-red-200 dark:border-red-800 text-center">
                    <WarningCircle size={32} className="mx-auto text-red-500 mb-2" />
                    <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
                    <Button onClick={() => LoadAllItems(filters.status)} className="mt-4" size="sm">
                        Tentar Novamente
                    </Button>
                </div>
            ) : items.length === 0 ? (
                <div className="text-center py-12 bg-mono-50 dark:bg-mono-900/20 rounded-xl border border-dashed border-mono-300 dark:border-mono-800">
                    <p className="text-mono-500 dark:text-mono-400 font-medium">
                        {filters.search
                            ? `Nenhum item encontrado para "${filters.search}" com os filtros atuais.`
                            : 'Nenhum item encontrado com os filtros atuais.'}
                    </p>
                </div>
            ) : (
                <>
                    <p className="text-xs text-mono-500">
                        <strong className="text-black dark:text-white">{FormatInt(totalFiltered)}</strong> anúncios com os
                        filtros atuais
                    </p>

                    <div className="flex flex-col gap-2 font-sans">
                        {items.map((item, idx) => (
                            <CampaignItemCard
                                key={item.item_id || idx}
                                item={item}
                                initialPanel={focus && focus.itemId === item.item_id ? focus.panel : null}
                            />
                        ))}
                    </div>

                    {/* Controles de Paginacao */}
                    <PaginationControls
                        currentPage={Math.floor(filters.offset / filters.limit) + 1}
                        totalPages={Math.ceil(totalFiltered / filters.limit)}
                        onPageChange={handlePageChange}
                        itemsPerPage={filters.limit}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        totalItems={totalFiltered}
                    />
                </>
            )}
        </div>
    );
};

export default CampaignMonitorItems;
